import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { Timer, Plus, History, ChevronDown } from "lucide-react";
import { useState, useEffect } from "react";
import axios from "axios";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeCompetitions, setActiveCompetitions] = useState([]);
  
  const isActive = (path) => {
    if (path === "/" && location.pathname === "/") return true;
    if (path !== "/" && location.pathname.startsWith(path)) return true;
    return false;
  };

  // Fetch active competitions (not completed)
  useEffect(() => {
    const fetchActive = async () => {
      try {
        const res = await axios.get(`${API}/competitions`);
        const active = res.data.filter(c => c.status !== "completed").slice(0, 5);
        setActiveCompetitions(active);
      } catch (e) {
        console.error("Error fetching competitions:", e);
      }
    };
    fetchActive();
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
              <Timer className="w-6 h-6" strokeWidth={2} />
              <span className="font-heading font-bold text-xl tracking-tight">
                Poetry Slam Scorekeeper
              </span>
            </Link>
            
            <div className="flex items-center gap-6">
              {/* Active Competitions Dropdown */}
              {activeCompetitions.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="font-heading font-medium text-sm" data-testid="nav-active-dropdown">
                      Active Competitions
                      <ChevronDown className="w-4 h-4 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {activeCompetitions.map((comp) => (
                      <DropdownMenuItem 
                        key={comp.id}
                        onClick={() => navigate(comp.status === "setup" ? `/setup/${comp.id}` : `/competition/${comp.id}`)}
                        data-testid={`nav-comp-${comp.id}`}
                      >
                        <div className="flex flex-col">
                          <span className="font-medium">{comp.name}</span>
                          <span className="text-xs text-muted-foreground capitalize">{comp.status}</span>
                        </div>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              
              <Link 
                to="/setup" 
                className={`nav-link font-heading font-medium text-sm flex items-center gap-1 ${isActive("/setup") ? "active" : ""}`}
                data-testid="nav-new-competition"
              >
                <Plus className="w-4 h-4" />
                New
              </Link>
              <Link 
                to="/history" 
                className={`nav-link font-heading font-medium text-sm flex items-center gap-1 ${isActive("/history") ? "active" : ""}`}
                data-testid="nav-history"
              >
                <History className="w-4 h-4" />
                History
              </Link>
            </div>
          </div>
        </div>
      </nav>
      
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <Outlet />
      </main>
    </div>
  );
}
